/**
 * World Cup Mobile Discipline (F2)
 * --------------------------------
 * Detects mobile-class environmental constraints — battery saver,
 * deviceMemory, low-end CPU, thermal proxy, visibility state — and exposes
 * a derived "discipline" snapshot consumed by edgeSafeFlags and the
 * worldCupResilienceOps panel.
 *
 * - Reuses MobileMatchMode FPS sampler (no new RAF loops).
 * - Listens to existing browser events: visibilitychange, battery change,
 *   reduced-motion media query, connection change.
 * - SSR-safe: every browser API guarded.
 * - No polling beyond what MobileMatchMode already runs.
 */
import { useSyncExternalStore } from "react";
import { getProfile, useMobileMatchMode } from "@/lib/launchHardening/mobileMatchMode";

export type ThermalProxy = "nominal" | "warm" | "hot";

export interface MobileDiscipline {
  /** True when the tab is currently hidden — animations should freeze. */
  hidden: boolean;
  /** Battery saver mode (heuristic: low battery + not charging, or saveData). */
  batterySaver: boolean;
  /** navigator.deviceMemory in GB (best-effort, defaults to 4). */
  deviceMemoryGb: number;
  /** Detected low-end device class. */
  lowEnd: boolean;
  /** Thermal proxy derived from sustained FPS + connection. */
  thermal: ThermalProxy;
  /** prefers-reduced-motion. */
  reducedMotion: boolean;
  /** Convenience degradation flags consumed by overlays. */
  shouldReduceGlow: boolean;
  shouldReduceBlur: boolean;
  shouldReduceAnimations: boolean;
  shouldReduceBackdrop: boolean;
  shouldReduceGradients: boolean;
  shouldReduceScreenshotFX: boolean;
}

const DEFAULT: MobileDiscipline = {
  hidden: false,
  batterySaver: false,
  deviceMemoryGb: 4,
  lowEnd: false,
  thermal: "nominal",
  reducedMotion: false,
  shouldReduceGlow: false,
  shouldReduceBlur: false,
  shouldReduceAnimations: false,
  shouldReduceBackdrop: false,
  shouldReduceGradients: false,
  shouldReduceScreenshotFX: false,
};

type BatteryManager = {
  level: number;
  charging: boolean;
  addEventListener?: (e: string, cb: () => void) => void;
};

let state: MobileDiscipline = DEFAULT;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

function derive(): MobileDiscipline {
  if (typeof window === "undefined") return DEFAULT;
  const mmm = getProfile();
  const nav = navigator as Navigator & {
    deviceMemory?: number;
    connection?: { saveData?: boolean; effectiveType?: string };
    hardwareConcurrency?: number;
  };
  const mem = typeof nav.deviceMemory === "number" ? nav.deviceMemory : 4;
  const cores = nav.hardwareConcurrency ?? 4;
  const saveData = !!nav.connection?.saveData;
  const effType = nav.connection?.effectiveType ?? "4g";
  const reducedMotion =
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

  const lowEnd = mem <= 2 || cores <= 2 || mmm.profile === "low";
  // Thermal proxy: sustained low FPS or saveData / slow network → warm/hot.
  let thermal: ThermalProxy = "nominal";
  if (mmm.fps > 0 && mmm.fps < 28) thermal = "hot";
  else if (mmm.fps > 0 && mmm.fps < 40) thermal = "warm";
  else if (saveData || effType === "2g" || effType === "slow-2g") thermal = "warm";

  const batterySaver = saveData || batteryLow || reducedMotion;

  const shouldReduceAnimations =
    reducedMotion || lowEnd || thermal === "hot" || hidden;
  const shouldReduceGlow = lowEnd || thermal !== "nominal" || batterySaver;
  const shouldReduceBlur = lowEnd || thermal === "hot" || batterySaver;
  const shouldReduceBackdrop = shouldReduceBlur;
  const shouldReduceGradients = lowEnd || thermal === "hot";
  const shouldReduceScreenshotFX = lowEnd || thermal === "hot";

  return {
    hidden,
    batterySaver,
    deviceMemoryGb: mem,
    lowEnd,
    thermal,
    reducedMotion,
    shouldReduceGlow,
    shouldReduceBlur,
    shouldReduceAnimations,
    shouldReduceBackdrop,
    shouldReduceGradients,
    shouldReduceScreenshotFX,
  };
}

let hidden = false;
let batteryLow = false;
let started = false;

function refresh() {
  const next = derive();
  // Cheap shallow-compare on the flags we care about.
  if (
    next.hidden !== state.hidden ||
    next.batterySaver !== state.batterySaver ||
    next.lowEnd !== state.lowEnd ||
    next.thermal !== state.thermal ||
    next.reducedMotion !== state.reducedMotion ||
    next.shouldReduceAnimations !== state.shouldReduceAnimations ||
    next.shouldReduceGlow !== state.shouldReduceGlow ||
    next.shouldReduceBlur !== state.shouldReduceBlur ||
    next.shouldReduceBackdrop !== state.shouldReduceBackdrop ||
    next.shouldReduceGradients !== state.shouldReduceGradients ||
    next.shouldReduceScreenshotFX !== state.shouldReduceScreenshotFX
  ) {
    state = next;
    emit();
  } else {
    state = next;
  }
}

function start() {
  if (started || typeof window === "undefined") return;
  started = true;
  hidden = document.hidden;
  state = derive();

  document.addEventListener("visibilitychange", () => {
    hidden = document.hidden;
    refresh();
  });

  const nav = navigator as Navigator & {
    getBattery?: () => Promise<BatteryManager>;
    connection?: { addEventListener?: (e: string, cb: () => void) => void };
  };
  nav.connection?.addEventListener?.("change", refresh);
  window
    .matchMedia?.("(prefers-reduced-motion: reduce)")
    .addEventListener?.("change", refresh);

  nav.getBattery?.().then((bat) => {
    const sync = () => {
      batteryLow = !bat.charging && bat.level < 0.2;
      refresh();
    };
    sync();
    bat.addEventListener?.("levelchange", sync);
    bat.addEventListener?.("chargingchange", sync);
  }).catch(() => { /* no battery API — leave defaults */ });

  // Piggyback on MobileMatchMode FPS updates (no new RAF loop): poll its
  // snapshot every 5s to refresh the thermal proxy.
  window.setInterval(refresh, 5_000);
}

if (typeof window !== "undefined") start();

export function getMobileDiscipline(): MobileDiscipline {
  return state;
}

export function useMobileDiscipline(): MobileDiscipline {
  // Re-derive when MobileMatchMode changes (FPS/profile shifts).
  useMobileMatchMode();
  return useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => { listeners.delete(cb); }; },
    () => state,
    () => state,
  );
}